import { spawnSync } from "node:child_process";

import { buildOrgxApiUrl, normalizeOrgxBaseUrl } from "./auth.js";
import { isRecord } from "./fs.js";

export type PairingStatus =
  | "pending"
  | "authorized"
  | "ready"
  | "consumed"
  | "expired"
  | "cancelled"
  | "failed";

export interface BrowserPairingStartResult {
  pairingId: string;
  pollToken: string;
  connectUrl: string;
  expiresAt: string;
  pollIntervalMs: number;
}

export interface BrowserPairingPollResult {
  pairingId: string;
  status: PairingStatus;
  expiresAt: string;
  workspaceName: string | null;
  keyPrefix: string | null;
  executionMode: "cloud" | "local" | null;
  errorCode: string | null;
  errorMessage: string | null;
  key?: string;
}

export interface BrowserPairingOpenResult {
  ok: boolean;
  error?: string;
}

interface FetchJsonOptions {
  body?: string | undefined;
  fetchImpl?: typeof fetch | undefined;
  method: string;
  url: string;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function parseJsonBody(text: string): unknown {
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function fetchJson<T>({
  url,
  method,
  body,
  fetchImpl = fetch,
}: FetchJsonOptions): Promise<T> {
  const response = await fetchImpl(url, {
    method,
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body } : {}),
    signal: AbortSignal.timeout(7_000),
  });

  const payload = parseJsonBody(await response.text());

  if (!response.ok) {
    const errorMessage = isRecord(payload)
      ? asString(payload.error) ?? asString(payload.message)
      : typeof payload === "string"
        ? payload
        : null;
    throw new Error(errorMessage ?? `HTTP ${response.status}`);
  }

  if (isRecord(payload) && payload.ok === true && "data" in payload) {
    return payload.data as T;
  }

  return payload as T;
}

const UNROUTABLE_HOSTNAMES = new Set(["0.0.0.0", "::", "[::]"]);

export function sanitizeConnectUrl(connectUrl: string, baseUrl?: string): string {
  try {
    const parsed = new URL(connectUrl);
    if (UNROUTABLE_HOSTNAMES.has(parsed.hostname)) {
      const base = new URL(normalizeOrgxBaseUrl(baseUrl));
      parsed.protocol = base.protocol;
      parsed.hostname = base.hostname;
      parsed.port = base.port;
    }
    return parsed.toString();
  } catch {
    return connectUrl;
  }
}

function parsePairingStartResult(value: unknown): BrowserPairingStartResult {
  if (!isRecord(value)) {
    throw new Error("OrgX pairing start returned an invalid response.");
  }

  const pairingId = asString(value.pairingId);
  const pollToken = asString(value.pollToken);
  const connectUrl = asString(value.connectUrl);
  const expiresAt = asString(value.expiresAt);
  const pollIntervalMs = typeof value.pollIntervalMs === "number"
    ? value.pollIntervalMs
    : Number.NaN;

  if (!pairingId || !pollToken || !connectUrl || !expiresAt || !Number.isFinite(pollIntervalMs)) {
    throw new Error("OrgX pairing start returned an incomplete response.");
  }

  return { pairingId, pollToken, connectUrl, expiresAt, pollIntervalMs };
}

function parsePairingPollResult(value: unknown): BrowserPairingPollResult {
  if (!isRecord(value)) {
    throw new Error("OrgX pairing poll returned an invalid response.");
  }

  const pairingId = asString(value.pairingId);
  const status = asString(value.status) as PairingStatus | null;
  const expiresAt = asString(value.expiresAt);

  if (!pairingId || !status || !expiresAt) {
    throw new Error("OrgX pairing poll returned an incomplete response.");
  }

  return {
    pairingId,
    status,
    expiresAt,
    workspaceName: asString(value.workspaceName),
    keyPrefix: asString(value.keyPrefix),
    executionMode: value.executionMode === "cloud" || value.executionMode === "local"
      ? value.executionMode
      : null,
    errorCode: asString(value.errorCode),
    errorMessage: asString(value.errorMessage),
    ...(asString(value.key) ? { key: asString(value.key)! } : {}),
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function startBrowserPairing(
  options: {
    baseUrl?: string | undefined;
    deviceName?: string | undefined;
    installationId: string;
    platform?: string | undefined;
  },
  fetchImpl?: typeof fetch,
): Promise<BrowserPairingStartResult> {
  const data = await fetchJson<unknown>({
    url: buildOrgxApiUrl("/plugin/openclaw/pairings", options.baseUrl),
    method: "POST",
    body: JSON.stringify({
      installationId: options.installationId,
      clientType: 'orgx-wizard',
      ...(options.deviceName ? { deviceName: options.deviceName } : {}),
      ...(options.platform ? { platform: options.platform } : {}),
    }),
    fetchImpl,
  });

  const result = parsePairingStartResult(data);
  return {
    ...result,
    connectUrl: sanitizeConnectUrl(result.connectUrl, options.baseUrl),
  };
}

export async function pollBrowserPairing(
  options: {
    baseUrl?: string | undefined;
    pairingId: string;
    pollToken: string;
  },
  fetchImpl?: typeof fetch,
): Promise<BrowserPairingPollResult> {
  const baseUrl = normalizeOrgxBaseUrl(options.baseUrl);
  const url = new URL(buildOrgxApiUrl(`/plugin/openclaw/pairings/${options.pairingId}`, baseUrl));
  url.searchParams.set("pollToken", options.pollToken);

  const data = await fetchJson<unknown>({
    url: url.toString(),
    method: "GET",
    fetchImpl,
  });

  return parsePairingPollResult(data);
}

export async function waitForBrowserPairing(
  options: {
    baseUrl?: string | undefined;
    pairingId: string;
    pollIntervalMs?: number | undefined;
    pollToken: string;
    timeoutMs?: number | undefined;
  },
  fetchImpl?: typeof fetch,
): Promise<BrowserPairingPollResult> {
  const timeoutMs = options.timeoutMs ?? 10 * 60_000;
  const pollIntervalMs = options.pollIntervalMs ?? 1_500;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    const result = await pollBrowserPairing(
      {
        baseUrl: options.baseUrl,
        pairingId: options.pairingId,
        pollToken: options.pollToken,
      },
      fetchImpl,
    );

    if (result.status === "ready") {
      if (!result.key) {
        throw new Error("OrgX pairing completed without returning an API key.");
      }
      return result;
    }

    if (result.status === "failed") {
      throw new Error(result.errorMessage ?? "OrgX pairing failed.");
    }

    if (result.status === "expired") {
      throw new Error("OrgX pairing expired before it was approved.");
    }

    if (result.status === "cancelled") {
      throw new Error("OrgX pairing was cancelled in the browser.");
    }

    if (result.status === "consumed") {
      throw new Error("OrgX pairing was already consumed by another client.");
    }

    await sleep(Math.min(5_000, Math.max(350, pollIntervalMs)));
  }

  throw new Error(
    `Timed out waiting for OrgX pairing after ${Math.round(timeoutMs / 1000)} seconds.`,
  );
}

export async function acknowledgeBrowserPairing(
  options: {
    baseUrl?: string | undefined;
    pairingId: string;
    pollToken: string;
  },
  fetchImpl?: typeof fetch,
): Promise<void> {
  await fetchJson<unknown>({
    url: buildOrgxApiUrl(`/plugin/openclaw/pairings/${options.pairingId}/ack`, options.baseUrl),
    method: "POST",
    body: JSON.stringify({ pollToken: options.pollToken }),
    fetchImpl,
  });
}

export function openBrowser(url: string): BrowserPairingOpenResult {
  const platform = process.platform;
  const result = platform === "darwin"
    ? spawnSync("open", [url], { stdio: "ignore" })
    : platform === "win32"
      ? spawnSync("cmd", ["/c", "start", "", url], { stdio: "ignore" })
      : spawnSync("xdg-open", [url], { stdio: "ignore" });

  if (result.error) {
    return { ok: false, error: result.error.message };
  }

  if (typeof result.status === "number" && result.status !== 0) {
    return { ok: false, error: `Browser open command exited with code ${result.status}.` };
  }

  return { ok: true };
}
